/**
 * «이메일 기억하기» 의 저장소.
 *
 * ## 기존 `useLocalStorage` 훅을 쓰지 않는 이유
 *
 * 세 가지가 이 용도에 안 맞는다.
 *
 * 1. **지우는 방법이 없다.** 체크를 해제하면 값이 *정말로* 없어져야 한다. 그 훅은
 *    `null` 을 JSON 으로 써 넣을 뿐 키를 남긴다. 공용 PC 에서 «껐는데 아직 있네» 가
 *    되면 이 기능은 안 넣느니만 못하다.
 * 2. **`JSON.parse` 에 try/catch 가 없다.** 시크릿 창이나 사이트 데이터 차단에서는
 *    `localStorage` 접근 자체가 던진다. 로그인 화면이 흰 화면이 된다.
 * 3. 타입이 없다(암묵적 any).
 *
 * 그 훅은 `SettingsContext` 가 쓰고 있어서 **건드리지 않는다.**
 *
 * ## 무엇을 저장하나
 *
 * **이메일 주소 하나뿐이다.** 토큰도 세션도 여기 두지 않는다 — 그건 `accessToken`
 * 이 하는 일이고, 그것과 섞이면 «기억하기» 를 끄는 것이 로그아웃처럼 보인다.
 *
 * 저장은 **사용자가 체크했을 때만** 한다. 기본은 꺼짐이다. 이메일은 개인정보고,
 * 이 화면은 공용 PC 에서도 열린다.
 */
const KEY = "rememberedEmail";

/**
 * «기억하기» 를 켜고 링크를 요청한 주소. **로그인이 실제로 되면** 그때 기억한다(f1-6).
 *
 * 예전에는 요청이 202 로 끝나면 바로 기억했다. 그런데 202 는 «보냈다» 가 아니다 — 서버는
 * 없는 주소에도 같은 답을 준다(열거 방어). 오타 주소(`@frism.co`)가 기억돼 다음에도 그대로
 * 채워졌다. 링크는 대개 **새 탭**에서 열리므로 탭 사이에 넘기려고 저장소에 둔다.
 */
const PENDING_KEY = "rememberedEmailPending";

/** 기억해 둔 주소. 없거나 읽을 수 없으면 빈 문자열. */
export function readRememberedEmail(): string {
  try {
    return localStorage.getItem(KEY) ?? "";
  } catch {
    // 시크릿 창·사이트 데이터 차단. 기억하지 못할 뿐 로그인은 되어야 한다.
    return "";
  }
}

/** 기억한다. 빈 값이면 지운다 — «빈 문자열을 기억» 하는 상태를 만들지 않는다. */
export function rememberEmail(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) {
    forgetEmail();
    return;
  }
  try {
    localStorage.setItem(KEY, trimmed);
  } catch {
    // 용량 초과·차단. 조용히 넘어간다. 여기서 막으면 로그인을 못 한다.
  }
}

/** 잊는다. 키 자체를 지운다. 기다리던 것도 같이 버린다. */
export function forgetEmail(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* 위와 같다 */
  }
}

/** 링크를 요청할 때 — 로그인이 되면 기억할 주소를 적어 둔다. */
export function rememberAfterLogin(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) return;
  try {
    localStorage.setItem(PENDING_KEY, trimmed);
  } catch {
    /* 차단된 브라우저 — 기억하지 못할 뿐이다 */
  }
}

/**
 * 로그인이 됐을 때 — 적어 둔 주소가 **지금 들어온 계정**과 같을 때만 기억한다. 다른 주소의
 * 링크로 들어왔으면(이메일 찾기 등) 적어 둔 것은 버린다.
 */
export function commitRememberedEmail(loggedInEmail: string | null | undefined): void {
  try {
    const pending = localStorage.getItem(PENDING_KEY);
    localStorage.removeItem(PENDING_KEY);
    if (pending && loggedInEmail && pending.toLowerCase() === loggedInEmail.trim().toLowerCase()) {
      localStorage.setItem(KEY, loggedInEmail.trim());
    }
  } catch {
    /* 위와 같다 */
  }
}
